'use client';

import { Button, Grid } from '@mui/material';
import Divider from '@mui/material/Divider';
import { type FC, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import CheckBoxGroup from 'molecules/Form/CheckboxGroup';
import Form from 'molecules/Form/Form';
import RichText from 'molecules/RichText';

import styles from 'components/Forms/AgentQuote/agentquote.module.scss';
import { specialties } from 'components/Forms/AgentQuote/data/specialties';
import { usStates } from 'components/Forms/AgentQuote/data/states';
import { validate } from 'components/Forms/AgentQuote/data/validation';

import { isNonEmptyArray } from 'utils/functions';
import { ENV_KEY_MAPS, getEnvValue } from 'utils/getEnvValue';
import SendGtmEvent from 'utils/hooks/sendGtmEvent';

import type { AgentQuoteProps } from 'components/Forms/AgentQuote/config';
import type { FormEvent } from 'react';
import type { ReCAPTCHAProps } from 'react-google-recaptcha';

const ReCAPTCHAComponent = ReCAPTCHA as unknown as React.ForwardRefExoticComponent<
  React.PropsWithoutRef<ReCAPTCHAProps> & React.RefAttributes<ReCAPTCHA>
>;

const AgentQuote: FC<AgentQuoteProps> = ({
  heading,
  formCoverages,
  disclaimer,
  information,
  threeInfo,
  contacts,
  redirect_url,
}) => {
  const reCaptcha = true;
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

  const initialFormData = useMemo(() => {
    const defaultValues: Record<string, boolean> = {};
    formCoverages?.options?.forEach(option => {
      if (option.label) {
        defaultValues[option.label] = false;
      }
    });

    return defaultValues;
  }, [formCoverages]);

  const formUrl = getEnvValue(redirect_url, ENV_KEY_MAPS.urls);

  const [formData, setFormData] = useState<Record<string, any>>(initialFormData);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'NPI') {
      let npiNumbers = e.target.value.split(/\s*,\s*/).map(item => item.trim());

      npiNumbers = [...new Set(npiNumbers)];
      e.target.value = npiNumbers.join(',');
    }

    if (e.target.name === 'Medical Professional Liability (indication form)') {
      setFormData({
        ...formData,
        ['NPI']: '',
        [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
      });
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'Medical Professional Liability (indication form)') {
      setFormData({ ...formData, ['NPI']: '', [e.target.name]: e.target.checked });
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.checked,
      });
    }
  };

  const handleCaptchaChange = async (token: string | null) => {
    try {
      if (!token) {
        setFormStatus('error');

        return;
      }

      const res = await fetch('/api/recaptcha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setCaptchaToken(token);
        setCaptchaError(null);
      } else {
        setCaptchaToken(null);
        setCaptchaError('Captcha verification failed. Please try again.');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    const honeypotValue = (event.target as HTMLFormElement).username?.value;
    if (honeypotValue && honeypotValue.trim() !== '') {
      event.preventDefault();
      setFormStatus('error');

      return;
    }

    if (reCaptcha && !captchaToken) {
      event.preventDefault();
      setCaptchaError('Please verify that you are not a robot.');
      setFormStatus('error');

      return;
    }
    const requiredFields = isNonEmptyArray(information?.blocks)
      ? information.blocks
          .flatMap(block => {
            const requiredField: string[] = [];

            if (block.leftSide?.required && block.leftSide?.fieldName) {
              requiredField.push(block.leftSide.fieldName);
            }

            if (block.rightSide?.required && block.rightSide?.fieldName) {
              requiredField.push(block.rightSide.fieldName);
            }

            return requiredField;
          })
          .filter(Boolean)
      : [];
    const missingFields = requiredFields.filter(field => !formData[field]);

    let valid = true;
    Object.entries(formData).forEach(([fieldName, value]) => {
      if (validate(false, '', value, fieldName, fieldName) !== '') {
        valid = false;
      }
    });

    if (missingFields.length > 0 || !valid) {
      event.preventDefault();
      setFormStatus('error');

      return;
    }
    setFormStatus('success');
  };

  const stateOptions = useMemo(
    () =>
      [{ abbreviation: '', name: '' }].concat(usStates).map(({ name, abbreviation }) => (
        <option key={abbreviation} value={abbreviation}>
          {name}
        </option>
      )),
    [],
  );

  const specialtyOptions = useMemo(() => {
    const orderedSpecialties = [''].concat(specialties.sort());

    return orderedSpecialties.map(s => (
      <option key={s} value={s}>
        {s}
      </option>
    ));
  }, [specialties]);

  return (
    <div className={styles.section}>
      {formStatus !== 'idle' && (
        <SendGtmEvent
          variant="agent-form"
          eventData={{
            formSpecialty: formData['Primary Specialty'],
            formState: formData['State'],
            formZip: formData['Zip'],
            formSubmitSuccessful: formStatus === 'success' ? true : false,
          }}
        />
      )}
      <div className={styles.container}>
        <div className={styles.heading}>
          {heading?.heading && <h1 className={styles.heading3}>{heading.heading}</h1>}
          {heading?.subheading && <h2 className={styles.heading4}>{heading.subheading}</h2>}
        </div>
        <form onSubmit={handleSubmit} action={formUrl} method="POST">
          <input
            type="text"
            name="username"
            id="username"
            className={styles.username}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          <div className={styles.formContainer}>
            <div className={styles.checkbox}>
              <CheckBoxGroup
                values={formData}
                label={formCoverages?.title}
                options={formCoverages?.options}
                onChange={handleCheckboxChange}
              />
            </div>
            <div className={styles.disclaimer}>{disclaimer}</div>
            <div className={styles.fieldset}>
              <legend>{information?.heading}</legend>
              <Grid sx={{ mt: '5px', mb: 3 }} item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Divider />
              </Grid>
              <div className={styles.block}>
                {isNonEmptyArray(information?.blocks) &&
                  information.blocks.map((block, index) => (
                    <div key={index}>
                      <div className={styles.twoColumn}>
                        {block?.leftSide?.fieldName && (
                          <div className={styles.col}>
                            <Form
                              onChange={handleInputChange}
                              validate={validate}
                              disabled={
                                block?.leftSide.fieldName === 'NPI' &&
                                !formData['Medical Professional Liability (indication form)']
                              }
                              value={formData[block?.leftSide.fieldName || '']}
                              {...block?.leftSide}
                            >
                              {block?.leftSide?.label === 'State' && stateOptions}
                              {block?.leftSide?.label === 'Primary Specialty' && specialtyOptions}
                            </Form>
                          </div>
                        )}
                        {block?.rightSide?.fieldName && (
                          <div className={styles.col}>
                            <Form
                              onChange={handleInputChange}
                              validate={validate}
                              disabled={
                                block?.rightSide.fieldName === 'NPI' &&
                                !formData['Medical Professional Liability (indication form)']
                              }
                              value={formData[block?.rightSide.fieldName || '']}
                              {...block?.rightSide}
                            >
                              {block?.rightSide?.label === 'State' && stateOptions}
                              {block?.rightSide?.label === 'Primary Specialty' && specialtyOptions}
                            </Form>
                          </div>
                        )}
                      </div>
                      {block?.addDivider && (
                        <Grid sx={{ mt: 3, mb: 3 }} item xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Divider />
                        </Grid>
                      )}
                    </div>
                  ))}
                {threeInfo && (
                  <div className={styles.threeInfo}>
                    <div className={styles.twoColumn}>
                      <div className={`${styles.col} ${styles.twoColumn}`}>
                        <div className={styles.col2}>
                          <Form
                            onChange={handleInputChange}
                            validate={validate}
                            value={formData[threeInfo?.field1?.fieldName || '']}
                            {...threeInfo?.field1}
                          />
                        </div>
                        <div className={styles.col2}>
                          <Form
                            onChange={handleInputChange}
                            validate={validate}
                            value={formData[threeInfo?.field2?.fieldName || '']}
                            {...threeInfo?.field2}
                          />
                        </div>
                      </div>
                      <div className={`${styles.col} ${styles.textarea}`}>
                        <Form
                          onChange={handleInputChange}
                          validate={validate}
                          value={formData[threeInfo?.field3?.fieldName || '']}
                          {...threeInfo?.field3}
                        />
                      </div>
                    </div>
                    {threeInfo?.addDivider && <hr className={styles.divider} />}
                  </div>
                )}
              </div>
            </div>
          </div>
          {reCaptcha && (
            <>
              <ReCAPTCHAComponent ref={recaptchaRef} sitekey={siteKey} onChange={handleCaptchaChange} />
              {captchaError && <p className={styles.recaptchaError}>{captchaError}</p>}
            </>
          )}
          <div className={styles.button}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                width: '40%',
                marginTop: '2rem',
                fontWeight: '400',
                fontSize: '1.7rem',
                backgroundColor: '#24272A',
                '&:hover': {
                  backgroundColor: 'black',
                },
              }}
              id="quote-form-submit-button"
            >
              Submit
            </Button>
          </div>
        </form>
        <div>{contacts && <RichText content={contacts} className={styles.richText} />}</div>
      </div>
    </div>
  );
};

export default AgentQuote;
